import { FunctionComponent, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { format } from "date-fns";

import { faTriangleExclamation } from "@fortawesome/pro-regular-svg-icons";

import { Icon, LoadingOverlay, Modal, PermissionCheck } from "@/components";

import { useTableSearch } from "@/utils";

import { PermissionRoles } from "@/enum";

import {
	useDeleteApiKeyMutation,
	useGetApiKeysQuery,
} from "@/pages/Private/redux/apiKey/apiKey.api";

import { Table, TableColumn } from "@/components/Table/Table";

import { Button } from "@/components/Button/Button";
import { ButtonColor } from "@/components/Button/types";

import { ApiKey } from "../schema/apiKey";

export const ApiKeyTable: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = useCallback((key: string) => t(`apiKey.${key}`), [t]);

	const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
	const [selectedApiKey, setSelectedApiKey] = useState<ApiKey | null>(null);
	const { page, setPage, limit, setLimit } = useTableSearch();
	const [deleteApiKey, { isLoading }] = useDeleteApiKeyMutation();

	const { data, isFetching } = useGetApiKeysQuery({
		page,
		limit,
	});

	const columns: TableColumn[] = useMemo(
		() => [
			{
				title: "Name",
				uid: "name",
				render: (row) => <div className="flex items-start justify-start">{row?.name}</div>,
			},
			{
				title: "API Key",
				uid: "apiKey",
				render: (row) => <div className="flex items-start justify-start">{row?.apiKey}</div>,
			},
			{
				title: "Issued at",
				uid: "createdAt",
				render: (row) => (
					<div className="flex items-start justify-start">
						{row?.createdAt && format(new Date(row?.createdAt), "MMM d, yyyy")}
					</div>
				),
			},
			{
				title: "",
				uid: "actions",
				render: (row) => (
					<div className="flex items-center justify-end">
						<PermissionCheck requiredPermissions={[PermissionRoles.API_KEY]}>
							<div>
								<Button
									className="text-neutral-500 hover:text-primary-main cursor-pointer"
									color={ButtonColor.DANGER_SECONDARY}
									title="Revoke"
									onClick={() => handleDeleteModal(row)}
								/>
							</div>
						</PermissionCheck>
					</div>
				),
			},
		],
		[ts]
	);

	const handleChangeRowsPerPage = (newLimit: number) => {
		setLimit(newLimit);
		setPage(1);
	};

	const handleDeleteModal = (row: ApiKey) => {
		setShowDeleteModal(true);
		setSelectedApiKey(row);
	};

	const handleDeleteApiKey = async () => {
		try {
			await deleteApiKey(selectedApiKey?.id ?? 0).unwrap();
			setShowDeleteModal(false);
		} catch (err) {
			console.error(err);
		}
	};

	return isFetching ? (
		<LoadingOverlay />
	) : (
		<div className="w-full">
			<div className="w-full bg-white">
				<Table
					columns={columns}
					data={data?.data || []}
					handleChangeRowsPerPage={handleChangeRowsPerPage}
					handleFetchPage={(page) => {
						setPage(page);
					}}
					limit={limit}
					overallCount={data?.meta?.itemCount}
					page={page}
					totalPages={data?.meta?.pageCount}
				/>
			</div>
			{showDeleteModal && (
				<Modal
					handleClose={() => setShowDeleteModal(false)}
					handleSave={handleDeleteApiKey}
					isLoading={isLoading}
					isOpened={showDeleteModal}
					submitButtonColor={ButtonColor.DANGER}
					submitButtonText={"Revoke"}
					title={"Revoke API Key"}
				>
					<div className="flex flex-row gap-4 items-start p-2 justify-start text-sm mb-2 text-text border border-status-warning rounded-md">
						<Icon className="text-status-warning w-4 h-4" icon={faTriangleExclamation} />
						You will be unable to perform any more actions with this API Key.
					</div>
				</Modal>
			)}
		</div>
	);
};
